import type { ExtractedDocxComment } from "@nevisign/reader-core";

export type CommentReply = {
  id: string;
  author: string;
  date: string;
  body: string;
};

export type PdfCommentAnchor = {
  page: number;
  xNorm: number;
  yNorm: number;
};

export type ReaderComment = ExtractedDocxComment & {
  replies?: CommentReply[];
  resolved?: boolean;
  deleted?: boolean;
  /** PDF pin position (normalized 0–1 within the page). */
  pdfAnchor?: PdfCommentAnchor;
};

type StoreShape = {
  comments: ReaderComment[];
};

function key(docName: string): string {
  return `nr-annotations:${docName}`;
}

export function loadLocalComments(docName: string): ReaderComment[] {
  try {
    const raw = localStorage.getItem(key(docName));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as StoreShape;
    return Array.isArray(parsed.comments) ? parsed.comments : [];
  } catch {
    return [];
  }
}

export function saveLocalComments(
  docName: string,
  comments: ReaderComment[]
): void {
  try {
    localStorage.setItem(
      key(docName),
      JSON.stringify({ comments } satisfies StoreShape)
    );
  } catch {
    /* quota */
  }
}

/** Merge DOCX-extracted comments with locally persisted overlays (replies/resolve/delete). */
export function mergeComments(
  fromDocx: ExtractedDocxComment[],
  local: ReaderComment[]
): ReaderComment[] {
  const byId = new Map<string, ReaderComment>();
  for (const c of fromDocx) {
    byId.set(c.word_id, { ...c, replies: [], resolved: false });
  }
  for (const c of local) {
    if (c.deleted) {
      byId.delete(c.word_id);
      continue;
    }
    const existing = byId.get(c.word_id);
    if (existing) {
      byId.set(c.word_id, {
        ...existing,
        ...c,
        body: c.body || existing.body,
        quote: c.quote || existing.quote,
        author: c.author || existing.author,
        date: c.date || existing.date,
        replies: c.replies ?? existing.replies ?? [],
        resolved: c.resolved ?? existing.resolved ?? false,
        pdfAnchor: c.pdfAnchor ?? existing.pdfAnchor,
      });
    } else if (c.word_id.startsWith("local-")) {
      byId.set(c.word_id, { ...c, replies: c.replies ?? [] });
    }
  }
  return Array.from(byId.values());
}
